import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Upload, FileText, AlertCircle, Scale, XCircle, Target, Users, Gavel, X, FileSpreadsheet, Download } from "lucide-react";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';
import LedProgressLine from "@/components/LedProgressLine";
import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Packer, Table, TableRow, TableCell, WidthType } from "docx";
import { saveAs } from "file-saver";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const getToken = () => {
  const token = localStorage.getItem('token');
  if (token) return token;
  const trialUser = localStorage.getItem('trial_user');
  if (trialUser) return 'trial_user_token';
  return null;
};

const isResultExpired = (createdAt) => {
  const created = new Date(createdAt);
  const now = new Date();
  const hoursDiff = (now - created) / (1000 * 60 * 60);
  return hoursDiff > 6; // Results expire after 6 hours
};

const DepositionPrep = ({ onBack }) => {
    // Set page title
  useEffect(() => {
    document.title = "Deposition Preparation - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  useEffect(() => {
    // Check if current results are expired
    if (analysisResults && analysisResults.created_at && isResultExpired(analysisResults.created_at)) {
      setAnalysisResults(null);
      toast.info('Previous results expired. Please generate a new analysis.');
    }
  }, []);

  const handleDownloadReport = async () => {
    if (!analysisResults) return;

    try {
      const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      };

      const children = [];

      // Title
      children.push(
        new Paragraph({
          text: "Deposition Preparation Report",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        })
      );

      // Case Overview
      children.push(
        new Paragraph({
          text: "Case Overview",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 }
        }),
        new Paragraph({
          text: stripHtml(analysisResults.summary),
          spacing: { after: 300 }
        })
      );

      // Key Facts
      if (analysisResults.key_facts && analysisResults.key_facts.length > 0) {
        children.push(
          new Paragraph({
            text: "Established Facts",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 }
          })
        );
        analysisResults.key_facts.forEach((fact, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${fact}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            })
          );
        });
      }

      // Inconsistencies
      if (analysisResults.inconsistencies && analysisResults.inconsistencies.length > 0) {
        children.push(
          new Paragraph({
            text: "Statement Inconsistencies",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 }
          })
        );
        analysisResults.inconsistencies.forEach((inc, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${inc.topic} (${inc.severity} Severity)`,
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              text: `Statement 1 (${inc.source1}): ${inc.statement1}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            }),
            new Paragraph({
              text: `Statement 2 (${inc.source2}): ${inc.statement2}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            }),
            new Paragraph({
              text: `Analysis: ${stripHtml(inc.analysis)}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            }),
            new Paragraph({
              text: `Suggested Question: "${inc.suggested_question}"`,
              spacing: { after: 200 },
              indent: { left: 720 }
            })
          );
        });
      }

      // Questioning Strategies
      if (analysisResults.questioning_strategies && analysisResults.questioning_strategies.length > 0) {
        children.push(
          new Paragraph({
            text: "Questioning Strategies",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 }
          })
        );
        analysisResults.questioning_strategies.forEach((strategy, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${strategy.topic}`,
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 }
            }),
            new Paragraph({
              text: `Rationale: ${strategy.rationale}`,
              spacing: { after: 100 },
              indent: { left: 720 }
            }),
            new Paragraph({
              text: "Suggested Questions:",
              spacing: { after: 50 },
              indent: { left: 720 }
            })
          );
          strategy.suggested_questions.forEach((q, qIndex) => {
            children.push(
              new Paragraph({
                text: `  ${qIndex + 1}. ${q}`,
                spacing: { after: 50 },
                indent: { left: 1440 }
              })
            );
          });
        });
      }

      // Footer
      children.push(
        new Paragraph({
          text: `Generated by Lawra on ${new Date().toLocaleDateString()}`,
          alignment: AlignmentType.CENTER,
          spacing: { before: 400 }
        })
      );

      const doc = new Document({
        sections: [{ properties: {}, children: children }]
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Deposition_Prep_${new Date().toISOString().split('T')[0]}.docx`);
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error downloading report. Please try again.');
    }
  };

  const handleFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (selectedFiles.length > 10) {
      toast.error("Error");
      return;
    }
    
    // Check file sizes
    for (const file of selectedFiles) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} exceeds 10MB limit`);
        return;
      }
    }
    
    setFiles(selectedFiles);
    setAnalysisResults(null);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    console.log('handleAnalyze called, files:', files);
    
    if (files.length === 0) {
      console.log('No files selected');
      toast.error("Error");
      return;
    }

    console.log('Starting analysis, API URL:', `${API}/due-diligence/analyze`);
    setAnalyzing(true);
    const formData = new FormData();
    
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });

    try {
      console.log('Sending request...');
      const response = await axios.post(
        `${API}/litigation/deposition-prep`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('Analysis response received:', response.data);
      setAnalysisResults(response.data);
      toast.success("Success");
    } catch (error) {
      console.error('Analysis error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      toast.error("Error");
    } finally {
      console.log('Analysis complete, setting analyzing to false');
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setAnalysisResults(null);
    if (document.getElementById('files-input')) {
      document.getElementById('files-input').value = '';
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'High': 'text-red-500',
      'Medium': 'text-yellow-500',
      'Low': 'text-green-500'
    };
    return colors[severity] || 'text-gray-500';
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <Scale size={40} className="tool-icon gold-icon" />
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <AnimatedToolTitle title="Deposition Preparation" />
              <span style={{
                fontSize: '0.7rem',
                fontWeight: '700',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#D4AF37',
                color: '#1a1a1a',
                borderRadius: '4px',
                letterSpacing: '0.05em',
                boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
              }}>
                BETA
              </span>
            </div>
            <p className="tool-subtitle">Analyze transcripts and statements to prepare for depositions and witness examination</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {!analysisResults ? (
          <>
            {/* Upload and Analysis Card */}
            <Card className="generation-form-card">
              <CardHeader>
                <CardTitle>Deposition Preparation & Analysis</CardTitle>
                <CardDescription>
                  Upload transcripts, statements, and related documents for comprehensive deposition preparation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="form-group">
                  <label className="upload-label">Transcripts & Statements (up to 10 files) *</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="files-input"
                      accept=".pdf,.docx,.txt"
                      multiple
                      onChange={handleFilesChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="files-input" className="file-upload-label">
                      <div className="upload-icon-tool">
                        {files.length > 0 ? <FileSpreadsheet size={48} /> : <Upload size={48} />}
                      </div>
                      <p className="upload-text-tool">
                        {files.length > 0 ? `${files.length} file(s) selected` : 'Click to upload transcripts'}
                      </p>
                      <p className="upload-subtext-tool">PDF, DOCX, TXT • Max 10MB per file</p>
                    </label>
                  </div>
                  
                  {/* Selected Files List */}
                  {files.length > 0 && (
                    <div className="selected-files-list">
                      {files.map((file, index) => (
                        <div key={index} className="selected-file-item">
                          <FileText size={16} />
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">({(file.size / 1024 / 1024).toFixed(1)}MB)</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                            className="remove-file-btn"
                          >
                            <XCircle size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <AnimatedActionButton
                    onClick={handleAnalyze} 
                    disabled={analyzing || files.length === 0}
                    loading={analyzing}
                    size="lg"
                  >
                    Prepare Deposition
                  </AnimatedActionButton>
                  <LedProgressLine isActive={analyzing} />
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          /* Results View */
          <>
            <div className="results-header">
              <h2>Deposition Preparation Report</h2>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button onClick={handleDownloadReport} variant="outline">
                  <Download size={18} style={{ marginRight: '8px' }} />
                  Download Report
                </Button>
                <Button onClick={handleReset} variant="outline">
                  New Preparation
                </Button>
              </div>
            </div>

            {/* Executive Summary */}
            <Card className="result-card">
              <CardHeader>
                <div className="risk-summary-header">
                  <Scale size={24} className="gold-icon" />
                  <div>
                    <CardTitle>Case Overview</CardTitle>
                    <CardDescription>Summary of witness statements and positions</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: analysisResults.summary }}
                />
              </CardContent>
            </Card>

            {/* Key Facts */}
            {analysisResults.key_facts && analysisResults.key_facts.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>Established Facts</CardTitle>
                  <CardDescription>Key facts consistently maintained across statements</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="differences-list">
                    {analysisResults.key_facts.map((fact, index) => (
                      <li key={index} className="difference-item">
                        {fact}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Inconsistencies */}
            {analysisResults.inconsistencies && analysisResults.inconsistencies.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>
                    <XCircle className="inline mr-2 text-red-500" size={24} />
                    Statement Inconsistencies
                  </CardTitle>
                  <CardDescription>Contradictions and discrepancies requiring attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="clauses-grid">
                    {analysisResults.inconsistencies.map((inconsistency, index) => (
                      <div key={index} className="clause-item">
                        <div className="clause-header">
                          <AlertCircle size={20} className={getSeverityColor(inconsistency.severity)} />
                          <h4>{inconsistency.topic}</h4>
                          <span className={`impact-badge ${getSeverityColor(inconsistency.severity)}`}>
                            {inconsistency.severity} Severity
                          </span>
                        </div>
                        
                        <div className="clause-comparison">
                          <div className="clause-section" style={{ overflowWrap: 'break-word', wordWrap: 'break-word', wordBreak: 'break-word' }}>
                            <strong>Statement 1 ({inconsistency.source1}):</strong>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{inconsistency.statement1}</p>
                          </div>
                          
                          <div className="clause-section" style={{ overflowWrap: 'break-word', wordWrap: 'break-word', wordBreak: 'break-word' }}>
                            <strong>Statement 2 ({inconsistency.source2}):</strong>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{inconsistency.statement2}</p>
                          </div>
                        </div>

                        <div className="clause-analysis">
                          <strong>Analysis:</strong>
                          <div 
                            className="analysis-content"
                            dangerouslySetInnerHTML={{ __html: inconsistency.analysis }}
                          />
                        </div>

                        <div className="suggested-question">
                          <strong>Suggested Question:</strong>
                          <p className="text-blue-500">"{inconsistency.suggested_question}"</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Questioning Strategies */}
            {analysisResults.questioning_strategies && analysisResults.questioning_strategies.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>
                    <Target className="inline mr-2 gold-icon" size={24} />
                    Questioning Strategies
                  </CardTitle>
                  <CardDescription>Strategic lines of questioning and areas to explore</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="clauses-grid">
                    {analysisResults.questioning_strategies.map((strategy, index) => (
                      <div key={index} className="clause-item">
                        <div className="clause-header">
                          <Target size={20} className="gold-icon" />
                          <h4>{strategy.topic}</h4>
                        </div>

                        <div className="clause-analysis">
                          <strong>Rationale:</strong>
                          <p>{strategy.rationale}</p>
                        </div>

                        <div className="suggested-questions">
                          <strong>Suggested Questions:</strong>
                          <ol className="questions-list">
                            {strategy.suggested_questions.map((question, qIndex) => (
                              <li key={qIndex} className="question-item">
                                {question}
                              </li>
                            ))}
                          </ol>
                        </div>

                        {strategy.potential_weaknesses && strategy.potential_weaknesses.length > 0 && (
                          <div className="weaknesses">
                            <strong>Potential Weaknesses:</strong>
                            <ul className="differences-list">
                              {strategy.potential_weaknesses.map((weakness, wIndex) => (
                                <li key={wIndex} className="difference-item text-yellow-500">
                                  {weakness}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
        <ReportIssueBar toolName="Deposition Prep" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Witness Credibility Assessment */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>
                  <Users className="inline mr-2 gold-icon" size={24} />
                  Witness Credibility Assessment
                </CardTitle>
                <CardDescription>Overall assessment of witness reliability and consistency</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: analysisResults.witness_credibility_assessment }}
                />
              </CardContent>
            </Card>

            {/* Preparation Recommendations */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Preparation Recommendations</CardTitle>
                <CardDescription>Strategic recommendations for deposition preparation</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: analysisResults.preparation_recommendations }}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default DepositionPrep;

